/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React, { FC, useMemo } from "react";
import { Accordion, HiddenVisually, Text, Title, Stack } from "@bookingcom/bui-react";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { SPACER_PADDED } from "@flights/constants";
import { UIFlightSegmentTimelined, UITimelineRow } from "@flights/types";
import { TimelineLeg } from "./TimelineLeg";
import { TimelineStop } from "./TimelineStop";
import Frame from "../../Frame";
import styles from "./TimelineSegment.module.css";
import useSearchCabinClass from "../../../../hooks/useSearchCabinClass";
import { AmenityProps } from "./TimelineLegInfo";
import { formattingUtils } from "@flights/web-api-utils-universal";
import FlightCardCarriers from "../../FlightCard/components/FlightCardCarriers";
import VIAlert from "../../VIAlert";
import useVirutalInterlining from "../../../../hooks/useVirutalInterlining";

type Props = {
  segment: UIFlightSegmentTimelined;
  segmentIndex: number;
  segmentVariant?: "fully-visible" | "accordion"; // flights_web_check_and_pay_accordion_fd
  hideCabinClassMismatch?: boolean;
  onAccordionOpen?: (index: number) => void;
} & AmenityProps; // flights_web_route_happy_amenities_ddot, flights_web_route_happy_amenities_mdot

export const TimelineSegment: FC<Props> = ({
  segment,
  segmentIndex,
  segmentVariant = "fully-visible",
  showAmenities = false,
  hideCabinClassMismatch,
  amenitiesVariant,
  onAmenitiesPopoverOpen,
  onAccordionOpen
}) => {
  const segmentHeader = (
    <Header segment={segment} segmentVariant={segmentVariant} hideCabinClassMismatch={hideCabinClassMismatch} />
  );

  const segmentContent = (
    <div className={styles.legsWrapper}>
      {segment.timeline.map((item, index) => (
        <Item
          data={item}
          isLastOne={index === segment.timeline.length - 1}
          key={index}
          prevTimeLineData={segment.timeline[index - 1]}
          nextTimeLineData={segment.timeline[index + 1]}
          idx={index}
          showAmenities={showAmenities}
          amenitiesVariant={amenitiesVariant}
          onAmenitiesPopoverOpen={onAmenitiesPopoverOpen}
        />
      ))}
    </div>
  );

  return (
    <Frame
      p={segmentVariant === "accordion" ? 4 : 0}
      className={segmentVariant === "accordion" ? styles.rootAccordion : styles.root}
      data-testid={`timeline_segment_${segmentIndex}`}
    >
      {segmentVariant === "accordion" ? (
        <Accordion
          titleContent={segmentHeader}
          onOpen={() => {
            onAccordionOpen?.(segmentIndex);
          }}
        >
          {segmentContent}
        </Accordion>
      ) : (
        <>
          {segmentHeader}
          {segmentContent}
        </>
      )}
    </Frame>
  );
};

const Header: FC<{
  segment: UIFlightSegmentTimelined;
  segmentVariant?: "fully-visible" | "accordion"; // flights_web_check_and_pay_accordion_fd exp
  hideCabinClassMismatch?: boolean;
}> = ({ segment, segmentVariant = "fully-visible", hideCabinClassMismatch = false }) => {
  const i18n = useI18n() as I18nChildContext;
  const { arrivalAirport, stops, durationMinutes } = segment;
  const { searchCabinClass } = useSearchCabinClass();
  const { isVirtualInterliningFlight } = useVirutalInterlining();

  const isCabinClassMismatch = useMemo(() => {
    if (hideCabinClassMismatch) {
      return false;
    }

    return segment.legs.some((l) => l.cabinClass !== searchCabinClass);
  }, [segment, searchCabinClass, hideCabinClassMismatch]);

  const headingText = useMemo(() => {
    if (segmentVariant === "accordion") {
      // flights_web_check_and_pay_accordion_fd
      return i18n.trans(
        t("flights_pb_flight_route_iata", {
          variables: {
            origin_city: segment.departureAirport.cityName ?? "",
            origin_iata: segment.departureAirport.code ?? "",
            destination_city: segment.arrivalAirport.cityName ?? "",
            destination_iata: segment.arrivalAirport.code ?? ""
          }
        })
      );
    } else {
      return i18n.trans(
        t("flights_details_route", {
          variables: {
            city_name: arrivalAirport.cityName || arrivalAirport.name
          }
        })
      );
    }
  }, [
    arrivalAirport,
    segmentVariant,
    i18n,
    segment.departureAirport.cityName,
    segment.departureAirport.code,
    segment.arrivalAirport.cityName,
    segment.arrivalAirport.code
  ]);

  const stopsText = useMemo(() => {
    if (stops === 0) {
      return i18n.trans(t("flights_filter_stops_none"));
    } else {
      return i18n.trans(t("flights_route_num_stops", { num_exception: stops, variables: { num_stops: stops } }));
    }
  }, [i18n, stops]);

  const durationText = useMemo(() => {
    return formattingUtils.flightDuration(durationMinutes, i18n);
  }, [i18n, durationMinutes]);

  const durationTextAria = useMemo(() => {
    return formattingUtils.flightDurationAria(durationMinutes, i18n);
  }, [i18n, durationMinutes]);

  const headerContent = (
    <Stack gap={1}>
      <Stack.Item>
        <Title
          variant="strong_1"
          attributes={{ "data-testid": "timeline_segment_title" }}
          title={headingText}
          titleTagName="h2"
        />
        <Text
          variant="body_2"
          className={styles.summary}
          attributes={{
            "data-testid": "timeline_segment_flight_duration",
            "aria-hidden": "true"
          }}
        >
          {stopsText}
          {SPACER_PADDED}
          {durationText}
          {segmentVariant === "accordion"
            ? `${SPACER_PADDED} ${formattingUtils.cabinClassFromSegment(segment, i18n)}`
            : isCabinClassMismatch
            ? ` ${i18n.trans(t("flights_fd_cabin_mismatch"))}`
            : null}
        </Text>
        <HiddenVisually>{`${stopsText} ${durationTextAria} ${
          isCabinClassMismatch ? i18n.trans(t("flights_fd_cabin_mismatch")) : ""
        }`}</HiddenVisually>
      </Stack.Item>

      {segmentVariant === "accordion" && isVirtualInterliningFlight(segment) && (
        <Stack.Item>
          <VIAlert segments={segment} />
        </Stack.Item>
      )}
    </Stack>
  );

  if (segmentVariant === "accordion") {
    // flights_web_check_and_pay_accordion_fd
    return (
      <Stack gap={4} direction="row" alignItems="center">
        <Stack.Item className={styles.segmentCarriers}>
          <FlightCardCarriers segment={segment} />
        </Stack.Item>
        <Stack.Item grow={true}>
          <div>{headerContent}</div>
        </Stack.Item>
      </Stack>
    );
  } else {
    return <Frame mb={4}>{headerContent}</Frame>;
  }
};

const Item: FC<
  {
    data: UITimelineRow;
    isLastOne?: boolean;
    prevTimeLineData: UITimelineRow;
    nextTimeLineData: UITimelineRow;
    idx: number;
  } & AmenityProps
> = ({
  data,
  isLastOne,
  prevTimeLineData,
  nextTimeLineData,
  idx,
  showAmenities = false,
  amenitiesVariant,
  onAmenitiesPopoverOpen
}) => {
  let view: React.ReactElement | null = null;

  if (data.type === "FLIGHT") {
    //  `shouldHideAnythingBeneath={true}` on last leg visually covers leftovers of
    //  `legsWrapper`'s dashed connection line at the bottom of segment
    view = (
      <TimelineLeg
        leg={data}
        shouldHideAnythingBeneath={isLastOne}
        idx={idx}
        showAmenities={showAmenities}
        amenitiesVariant={amenitiesVariant}
        onAmenitiesPopoverOpen={onAmenitiesPopoverOpen}
      />
    );
  } else if (data.type === "STOP") {
    view = <TimelineStop stop={data} prevLegData={prevTimeLineData} nextLegData={nextTimeLineData} />;
  }

  return view;
};
